import { createCtx } from "../utilities/contextHelper";
import { createAction } from "../utilities/createReducerAction";
import { PeriodTypes } from "../constants/periodTypes";
import {
  adjustMonths,
  adjustDateOnPeriodTypeChange,
  getSearchPeiodV2,
} from "../utilities/dateHelpers";
import { useEffect, useReducer, useState } from "react";
import dayjs from "dayjs";
import UrlSearchParams from "../constants/urlSearchParams";
import { UserSite } from "../types/user";
import {
  parseDateParam,
  parsePeriodTypeParam,
  parseSiteIdParam,
} from "../utilities/urlParamsHelpers";
import { useSearchParamsWithFallback } from "../utilities/useSearchParamsWithFallback";
import {
  DishSalesForecast,
  ManualForecastChange,
} from "../types/dishSalesForecast";
import { ChangeTracking } from "../types/changeTracking";
import { recalculateForecastOnChange } from "../utilities/forecastHelpers";
import { useUser } from "./userProvider";

export type ForecastState = {
  selectedSite?: UserSite;
  periodType: PeriodTypes;
  selectedDate: Date;
  searchPeriod: { from: string; to: string };
  forecastChangeTracking?: ChangeTracking<
    DishSalesForecast,
    ManualForecastChange
  >;
};

export enum ForecastStateEvents {
  SITE_CHANGE,
  PERIOD_TYPE_CHANGE,
  DATE_CHANGE,
  MANUAL_FORECAST_CHANGE,
  PERIOD_AND_DATE_CHANGE,
}

const siteChangeEvent = (site: UserSite) =>
  createAction(ForecastStateEvents.SITE_CHANGE, site);
const periodTypeChangeEvent = (periodType: PeriodTypes) =>
  createAction(ForecastStateEvents.PERIOD_TYPE_CHANGE, periodType);
const dateChangeEvent = (date: Date) =>
  createAction(ForecastStateEvents.DATE_CHANGE, date);
const manualForecastChangeEvent = (
  params:
    | { original: DishSalesForecast; change: ManualForecastChange }
    | undefined
) => createAction(ForecastStateEvents.MANUAL_FORECAST_CHANGE, params);
const periodAndDateChangeEvent = (periodType: PeriodTypes, date: Date) =>
  createAction(ForecastStateEvents.PERIOD_AND_DATE_CHANGE, {
    periodType,
    date,
  });

type SiteChangeEvent = ReturnType<typeof siteChangeEvent>;
type PeriodTypeChangeEvent = ReturnType<typeof periodTypeChangeEvent>;
type DateChangeEvent = ReturnType<typeof dateChangeEvent>;
type ManualForecastChangeEvent = ReturnType<typeof manualForecastChangeEvent>;
type PeriodAndDateChangeEvent = ReturnType<typeof periodAndDateChangeEvent>;

export type ForecastStateActions =
  | SiteChangeEvent
  | PeriodTypeChangeEvent
  | DateChangeEvent
  | ManualForecastChangeEvent
  | PeriodAndDateChangeEvent;

const initialForecastState: ForecastState = {
  periodType: PeriodTypes.DAILY,
  selectedDate: new Date(),
  searchPeriod: getSearchPeiodV2(new Date(), PeriodTypes.DAILY),
};

export const forecastReducer = (
  state: ForecastState,
  action: ForecastStateActions
): ForecastState => {
  switch (action.type) {
    case ForecastStateEvents.SITE_CHANGE:
      return {
        ...state,
        selectedSite: action.payload,
        forecastChangeTracking: undefined,
      };
    case ForecastStateEvents.PERIOD_TYPE_CHANGE:
      const updatedDate = adjustDateOnPeriodTypeChange(action.payload);
      return {
        ...state,
        periodType: action.payload,
        selectedDate: updatedDate,
        searchPeriod: getSearchPeiodV2(updatedDate, action.payload),
        forecastChangeTracking: undefined,
      };
    case ForecastStateEvents.DATE_CHANGE:
      return {
        ...state,
        selectedDate: action.payload,
        searchPeriod: getSearchPeiodV2(action.payload, state.periodType),
        forecastChangeTracking: undefined,
      };
    case ForecastStateEvents.MANUAL_FORECAST_CHANGE:
      if (!action.payload) {
        return { ...state, forecastChangeTracking: undefined };
      }
      const forecastChange: ChangeTracking<
        DishSalesForecast,
        ManualForecastChange
      > = !state.forecastChangeTracking
        ? {
            original: action.payload.original,
            updated: recalculateForecastOnChange(
              action.payload.original,
              action.payload.change
            ),
            changes: [action.payload.change],
          }
        : {
            ...state.forecastChangeTracking,
            updated: recalculateForecastOnChange(
              state.forecastChangeTracking!.updated!,
              action.payload.change
            ),
            changes: [
              ...state.forecastChangeTracking.changes,
              action.payload.change,
            ],
          };
      return { ...state, forecastChangeTracking: forecastChange };
    case ForecastStateEvents.PERIOD_AND_DATE_CHANGE:
      return {
        ...state,
        periodType: action.payload.periodType,
        selectedDate: action.payload.date,
        searchPeriod: getSearchPeiodV2(
          action.payload.date,
          action.payload.periodType
        ),
        forecastChangeTracking: undefined,
      };
    default:
      throw new Error(`Unknown action type: ${action}`);
  }
};

type FoecastProviderProps = {
  children: React.ReactNode;
};

interface ForecastContextType {
  forecastState: ForecastState;
  modalVisible: boolean;
  onPeriodChange: (periodType: PeriodTypes) => void;
  onMonthChange: (offset: number) => void;
  onDateChange: (date: Date) => void;
  onSiteChange: (siteId: string) => void;
  onManualForecastChange: (
    params:
      | {
          original: DishSalesForecast;
          change: ManualForecastChange;
        }
      | undefined
  ) => void;
  setModalVisible: (visible: boolean) => void;
  onPeriodAndDateChange: (periodType: PeriodTypes, date: Date) => void;
}

export const [useForecastContext, CtxProvider] =
  createCtx<ForecastContextType>();

export const ForecastProvider: React.FC<FoecastProviderProps> = ({
  children,
}) => {
  const { currentUser } = useUser();
  const { params, setSearchParamsifNeeded } = useSearchParamsWithFallback({
    [UrlSearchParams.PERIOD_TYPE]: {
      default: PeriodTypes.DAILY,
      parser: parsePeriodTypeParam,
    },
    [UrlSearchParams.DATE]: {
      default: new Date(Date.now()),
      parser: parseDateParam,
    },
    [UrlSearchParams.SITE_ID]: {
      default: currentUser!.siteAccess[0],
      parser: parseSiteIdParam,
      validation: currentUser!.siteAccess,
    },
  });

  const [forecastState, dispatchForecastState] = useReducer(forecastReducer, {
    ...initialForecastState,
    periodType: params.periodType,
    selectedDate: params.date,
    selectedSite: params.siteId,
    searchPeriod: getSearchPeiodV2(params.date, params.periodType),
  });
  const [modalVisible, setModalVisible] = useState(false);

  const onPeriodChange = (periodType: PeriodTypes) => {
    dispatchForecastState(periodTypeChangeEvent(periodType));
  };

  const onSiteChange = (siteId: string) => {
    dispatchForecastState(
      siteChangeEvent(
        currentUser!.siteAccess.find((s) => s.siteId === siteId) ??
          currentUser!.siteAccess[0]
      )
    );
  };

  const onDateChange = (date: Date) => {
    dispatchForecastState(dateChangeEvent(date));
  };

  // to-do decide if the fn call to adjustMonths shoudl be here or in the reducer
  const onMonthChange = (offset: number) => {
    dispatchForecastState(
      dateChangeEvent(adjustMonths(forecastState.selectedDate!, offset))
    );
  };

  const onManualForecastChange = (
    params:
      | { original: DishSalesForecast; change: ManualForecastChange }
      | undefined
  ) => {
    dispatchForecastState(manualForecastChangeEvent(params));
  };

  const onPeriodAndDateChange = (periodType: PeriodTypes, date: Date) => {
    dispatchForecastState(periodAndDateChangeEvent(periodType, date));
  };

  // keep state changes sync'd with URL params
  useEffect(() => {
    setSearchParamsifNeeded({
      [UrlSearchParams.PERIOD_TYPE]: forecastState.periodType.toString(),
      [UrlSearchParams.DATE]: dayjs(forecastState.selectedDate).format(
        "YYYY-MM-DD"
      ),
      [UrlSearchParams.SITE_ID]: forecastState.selectedSite?.siteId || "",
    });
  }, [
    forecastState.periodType,
    forecastState.selectedDate,
    forecastState.selectedSite?.siteId,
    setSearchParamsifNeeded,
  ]);

  return (
    <CtxProvider
      value={{
        forecastState,
        modalVisible,
        onPeriodChange,
        onDateChange,
        onMonthChange,
        onSiteChange,
        onManualForecastChange,
        setModalVisible,
        onPeriodAndDateChange,
      }}
    >
      {children}
    </CtxProvider>
  );
};
